/**
 * `.colorMultiply(_:)`: CSS has no filter that multiplies by a color, so the
 * page keeps one SVG filter per color in a hidden `<svg>` and the view's box
 * names it in its `filter` (`url(#sb-multiply-…)`). The filter is an
 * `feColorMatrix` that scales red, green, blue and alpha by the color's, in
 * sRGB as SwiftUI multiplies. A semantic or catalog color is resolved for the
 * scheme the page shows when the style is applied.
 */

import type { Color, RGBAColor } from './protocol';
import { colorToCSS } from './styles';

const SVG_NS = 'http://www.w3.org/2000/svg';
const DEFS_ID = 'sb-filter-defs';

function clamp01(v: number): number {
  return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 1;
}

function isRGBA(color: Color): color is RGBAColor {
  return typeof (color as RGBAColor).r === 'number';
}

/** The filter's id for a color: its components to three places. */
export function multiplyFilterId(color: RGBAColor): string {
  const part = (v: number) => Math.round(clamp01(v) * 1000).toString(36);
  return `sb-multiply-${part(color.r)}-${part(color.g)}-${part(color.b)}-${part(color.a ?? 1)}`;
}

/** The `feColorMatrix` values that multiply by `color`. */
export function multiplyMatrix(color: RGBAColor): string {
  const v = (n: number) => String(Math.round(clamp01(n) * 1000) / 1000);
  return [
    `${v(color.r)} 0 0 0 0`,
    `0 ${v(color.g)} 0 0 0`,
    `0 0 ${v(color.b)} 0 0`,
    `0 0 0 ${v(color.a ?? 1)} 0`,
  ].join(' ');
}

/** Parses a computed `rgb()` / `rgba()` / `color(srgb …)` value. */
export function parseComputedColor(value: string): RGBAColor | null {
  const rgb = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+))?\s*\)$/.exec(value.trim());
  if (rgb) {
    return { r: Number(rgb[1]) / 255, g: Number(rgb[2]) / 255, b: Number(rgb[3]) / 255, a: rgb[4] != null ? Number(rgb[4]) : 1 };
  }
  const srgb = /^color\(srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+))?\s*\)$/.exec(value.trim());
  if (srgb) return { r: Number(srgb[1]), g: Number(srgb[2]), b: Number(srgb[3]), a: srgb[4] != null ? Number(srgb[4]) : 1 };
  return null;
}

/** The color's components as `el` would draw it (a semantic or catalog color through its CSS). */
function resolve(color: Color, el: HTMLElement): RGBAColor {
  if (isRGBA(color)) return color;
  const probe = el.ownerDocument.createElement('span');
  probe.style.display = 'none';
  probe.style.color = colorToCSS(color);
  el.appendChild(probe);
  const computed = getComputedStyle(probe).color;
  probe.remove();
  return parseComputedColor(computed) ?? { r: 1, g: 1, b: 1, a: 1 };
}

/** The `filter` function that multiplies `el`'s box by `color`, adding the filter to the document once. */
export function colorMultiplyFilter(color: Color, el: HTMLElement): string {
  const rgba = resolve(color, el);
  const id = multiplyFilterId(rgba);
  const doc = el.ownerDocument;
  if (!doc.getElementById(id)) {
    let defs: Element | null = doc.getElementById(DEFS_ID);
    if (!defs) {
      const svg = doc.createElementNS(SVG_NS, 'svg');
      svg.setAttribute('aria-hidden', 'true');
      svg.setAttribute('width', '0');
      svg.setAttribute('height', '0');
      svg.style.position = 'absolute';
      const created = doc.createElementNS(SVG_NS, 'defs');
      created.id = DEFS_ID;
      svg.appendChild(created);
      defs = created;
      doc.body.appendChild(svg);
    }
    const filter = doc.createElementNS(SVG_NS, 'filter');
    filter.id = id;
    filter.setAttribute('color-interpolation-filters', 'sRGB');
    const matrix = doc.createElementNS(SVG_NS, 'feColorMatrix');
    matrix.setAttribute('type', 'matrix');
    matrix.setAttribute('values', multiplyMatrix(rgba));
    filter.appendChild(matrix);
    defs.appendChild(filter);
  }
  return `url(#${id})`;
}
